// Choosing your Director (Key Light Director): six film-light characters, a name of your own and any voice from the
// catalogue. Saved per user (PUT /api/director/persona); a live call switches over straight away (a new voice
// reconnects like switch_director_voice; a new name is passed to the Director as an [app] line).
// <DirectorTag /> is the Director's name tag in the corner of the Home talk stage; it opens the picker in a sheet.
import { useMemo, useState, type FormEvent } from "react";
import { tapTool } from "../actions";
import { api } from "../api";
import { toggleAudio } from "../audio";
import { director, switchVoice } from "../director";
import { cleanName, MAX_NAME, normPersona, presetOf, PRESETS, type Persona, type PersonaChange, type PresetId } from "../directors";
import { get, set, toast, useStore, type DirectorStatus } from "../store";
import type { VoiceInfo } from "../types";
import { Icon, Sheet, useLoad } from "./bits";
import { DirectorLight } from "./DirectorLight";

/** The shared audition line (api SAMPLE_LINES.director): one recording serves everyone, so hearing it is free. */
export const DIRECTOR_SAMPLE_LINE = "Hi, I'm your director. Tell me what we're making, and I'll take it from there.";

/** The user's Director (Key Light until /api/me says otherwise). */
export function usePersona(): Persona {
  const raw = useStore((s) => s.me?.user.director);
  const vid = useStore((s) => s.me?.user.directorVoiceId ?? null);
  const vname = useStore((s) => s.me?.user.directorVoiceName ?? null);
  return useMemo(() => raw ?? normPersona(undefined, { directorVoiceId: vid, directorVoiceName: vname }), [raw, vid, vname]);
}

/** Saves a change and brings a live call along with it. Returns the new persona, or null when it failed. */
export async function changeDirector(change: PersonaChange): Promise<Persona | null> {
  try {
    const r = await api.setDirectorPersona(change);
    const me = get().me;
    if (me) set({ me: { ...me, user: { ...me.user, director: r.persona, directorVoiceId: r.persona.customVoice, directorVoiceName: r.persona.customVoice ? r.persona.voiceName : null } } });
    if (director.connected()) {
      if (r.nameChanged) director.context(`[app] The user renamed you in Settings: your name is ${r.persona.name} now. Use it from here on; don't make a fuss about it.`);
      else if (change.preset) director.context(`[app] The user picked ${presetOf(r.persona.preset).name} (${presetOf(r.persona.preset).light}) as your look in Settings.`);
      if (r.voiceChanged) void switchVoice(r.persona.voiceId, r.persona.voiceName ?? undefined);
    }
    return r.persona;
  } catch (e) {
    toast((e as Error).message || "Couldn't save that.", "bad");
    return null;
  }
}

export function DirectorPicker(_props: { status?: DirectorStatus } = {}) {
  const persona = usePersona();
  const preset = presetOf(persona.preset);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [sampling, setSampling] = useState(false);
  const playing = useStore((s) => s.playing === "dpick-sample");
  const { data: voices } = useLoad<VoiceInfo[]>(() => api.voices(), []);

  const apply = async (change: PersonaChange) => {
    setBusy(true);
    await changeDirector(change);
    setBusy(false);
  };

  const nameValue = draft ?? (persona.customName ? persona.name : "");
  const cleaned = nameValue.trim() ? cleanName(nameValue) : null;
  const nameDirty = draft !== null && (cleaned ?? "") !== (persona.customName ? persona.name : "");
  const saveName = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!nameDirty) return;
    if (nameValue.trim() && !cleaned) return toast("Use letters or numbers for the name.", "bad");
    await apply({ name: cleaned });
    setDraft(null);
  };

  const hear = async () => {
    if (playing) return toggleAudio("dpick-sample", null);
    setSampling(true);
    const r = await tapTool("voice_sample", { voice_id: persona.voiceId, text: DIRECTOR_SAMPLE_LINE, for: "director" }, `played a sample of the Director voice ${persona.voiceName ?? ""}`.trim(), { keepCards: true });
    setSampling(false);
    const url = r?.cards[0]?.url;
    if (url) toggleAudio("dpick-sample", url);
    else if (r) toast("No sample came back for that voice.", "bad");
  };

  const voiceList = (voices ?? []).filter((v) => v.voiceId !== persona.presetVoiceId);
  const ownName = (voices ?? []).find((v) => v.voiceId === persona.presetVoiceId)?.name ?? preset.voiceName;
  const customKnown = !persona.customVoice || voiceList.some((v) => v.voiceId === persona.customVoice);

  return (
    <div className="dpick">
      <ul className="dpick-list" role="radiogroup" aria-label="Your Director">
        {PRESETS.map((p) => {
          const on = p.id === persona.preset;
          return (
            <li key={p.id}>
              <button data-action="director.pick" type="button" role="radio" aria-checked={on} className="dpick-row-btn" disabled={busy && !on} onClick={() => !on && void apply({ preset: p.id as PresetId })}>
                <span className="dpick-radio" aria-hidden>
                  {on && <Icon name="check" size={12} />}
                </span>
                <span className="dpick-text">
                  <span className="dpick-head">
                    <span className="dpick-name">{p.name}</span>
                    <span className="dpick-voice">{p.voiceName}</span>
                  </span>
                  <span className="dpick-tag">{p.tagline}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="dpick-row">
        <label className="field-label" htmlFor="dpick-name">
          What you call it
        </label>
        <form className="dpick-name-form field" onSubmit={(e) => void saveName(e)}>
          <input
            id="dpick-name"
            value={nameValue}
            maxLength={MAX_NAME + 8}
            placeholder={preset.name}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void saveName()}
            autoComplete="off"
            enterKeyHint="done"
            aria-describedby="dpick-name-hint"
          />
          <button data-action="director.rename" type="submit" className="btn btn-quiet" disabled={!nameDirty || busy}>
            Save
          </button>
        </form>
        <p id="dpick-name-hint" className="dpick-note">
          {persona.customName ? (
            <>
              Your Director answers to {persona.name}.{" "}
              <button data-action="director.rename" type="button" className="linkish" onClick={() => void apply({ name: null })} disabled={busy}>
                Go back to {preset.name}
              </button>
            </>
          ) : (
            `Leave it empty to keep ${preset.name}. Up to ${MAX_NAME} characters.`
          )}
        </p>
      </div>

      <div className="dpick-row">
        <label className="field-label" htmlFor="dpick-voice">
          Voice
        </label>
        <div className="dpick-voice-row">
          <label className="select">
            <select id="dpick-voice" value={persona.customVoice ?? ""} disabled={busy} onChange={(e) => void apply({ voiceId: e.target.value || null })}>
              <option value="">
                {preset.name}’s own voice ({ownName})
              </option>
              {!customKnown && persona.customVoice && <option value={persona.customVoice}>{persona.voiceName ?? "Your pick"}</option>}
              {voiceList.map((v) => (
                <option key={v.voiceId} value={v.voiceId}>
                  {v.name}: {v.description}
                </option>
              ))}
            </select>
          </label>
          <button data-action="voice.sample" type="button" className={`btn btn-quiet dpick-hear${playing ? " is-on" : ""}`} onClick={() => void hear()} disabled={sampling}>
            <Icon name={playing ? "pause" : sampling ? "wave" : "play"} size={16} /> {playing ? "Stop" : sampling ? "Recording" : `Hear ${persona.voiceName ?? "it"}`}
          </button>
        </div>
        <p className="dpick-note">Changing it during a call switches over in a second or two, and your Director picks up where you were.</p>
      </div>
    </div>
  );
}

/** Home: the Director's name tag in the corner of the talk stage. Opens the picker. */
export function DirectorTag() {
  const persona = usePersona();
  const status = useStore((s) => s.director);
  const [open, setOpen] = useState(false);
  const fresh = !persona.chosen;
  return (
    <>
      <button type="button" className={`director-tag${fresh ? " is-new" : ""}`} onClick={() => setOpen(true)} aria-label={`Your Director, ${persona.name}. Change your Director`}>
        <span className="director-tag-light">
          <DirectorLight preset={persona.preset} status={status} size={34} />
        </span>
        <span className="director-tag-text">
          <span className="director-tag-name">{persona.name}</span>
          <span className="director-tag-sub">{fresh ? "Meet your Director" : "Your Director · Change"}</span>
        </span>
      </button>
      {open && (
        <Sheet title="Your Director" onClose={() => setOpen(false)} className="dpick-sheet" wide>
          <div className="sheet-body">
            <p className="muted">Pick who you talk to. Each Director has its own voice and manner; name it whatever you like.</p>
            <DirectorPicker status={status} />
          </div>
        </Sheet>
      )}
    </>
  );
}
